// This file provides compatibility wrappers for Next.js components to work with Wouter
import React, { useCallback, useEffect } from 'react';
import { Link as WouterLink, useLocation, useRouter as useWouterRouter } from 'wouter';

// Next.js Link compatibility wrapper
export function Link({ href, className, children, ...props }: { 
  href: string; 
  className?: string; 
  children: React.ReactNode; 
  [key: string]: any 
}) {
  // Wouter's Link automatically renders an anchor tag, so we shouldn't wrap with another <a>
  return (
    <WouterLink href={href} className={className} {...props}>
      {children}
    </WouterLink>
  );
}

// Optimized Next.js Image compatibility wrapper
export function Image({
  src,
  alt,
  width,
  height,
  className,
  fill,
  sizes,
  priority,
  quality = 75,
  placeholder,
  blurDataURL,
  ...props
}: {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  fill?: boolean;
  sizes?: string;
  priority?: boolean;
  quality?: number;
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  [key: string]: any;
}) {
  // Handle fill prop (Next.js specific)
  const imageStyle = fill ? {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const
  } : undefined;

  // Create low-quality image URL by adding quality parameter if it's from an external source or using webp format
  const isExternalImage = src.startsWith('http');
  const optimizedSrc = isExternalImage && src.includes('unsplash.com')
    ? `${src}&q=${quality}&auto=format&w=${width || 800}`
    : src;

  // Placeholder effect
  const [isLoaded, setIsLoaded] = React.useState(priority);
  const [error, setError] = React.useState(false);

  // Define placeholder styles
  const placeholderStyles = {
    filter: isLoaded ? 'none' : 'blur(10px)',
    transition: 'filter 0.5s ease-in-out',
    // Scale slightly up when blurred to prevent edges from showing
    transform: isLoaded ? 'scale(1)' : 'scale(1.05)',
    opacity: error ? 0.5 : 1,
  };

  return (
    <div className={`image-container relative ${!fill ? 'inline-block' : ''}`} style={imageStyle}>
      {placeholder === "blur" && blurDataURL && !isLoaded && (
        <div
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{
            backgroundImage: `url(${blurDataURL})`,
            filter: 'blur(20px)',
            transform: 'scale(1.2)'
          }}
        />
      )}
      <img
        src={optimizedSrc}
        alt={alt}
        width={width}
        height={height}
        className={`${className || ''} transition-all duration-500`}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        style={{
          ...imageStyle,
          ...placeholderStyles
        }}
        onLoad={() => setIsLoaded(true)}
        onError={() => setError(true)}
        {...props}
      />
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 text-gray-500 text-sm p-2 text-center">
          {alt || 'Imagen no disponible'}
        </div>
      )}
    </div>
  );
}

// Next.js router compatibility wrapper
export function useRouter() {
  const [location, setLocation] = useWouterRouter();
  
  const push = useCallback((url: string) => {
    setLocation(url);
  }, [setLocation]);
  
  const replace = useCallback((url: string) => {
    // In a simplistic implementation, replace and push behave the same
    setLocation(url);
  }, [setLocation]);
  
  const back = useCallback(() => {
    window.history.back();
  }, []);
  
  const forward = useCallback(() => {
    window.history.forward();
  }, []);
  
  const prefetch = useCallback((_url: string) => {
    // No prefetching in this simplified implementation
    return Promise.resolve();
  }, []);
  
  return {
    push,
    replace,
    back,
    forward,
    prefetch,
    pathname: location,
    query: {}, // Simplified implementation with no query support
  };
}

// Next.js notFound function compatibility
export function notFound(): never {
  // Redirect to 404 page
  window.location.href = '/not-found';
  throw new Error('Page not found');
}

// Metadata type for Next.js compatibility
export type Metadata = {
  title?: string;
  description?: string;
  keywords?: string | string[];
  openGraph?: any;
  twitter?: any;
  [key: string]: any;
};

// Font compatibility - simple interface that doesn't actually load fonts
export const Inter = {
  className: 'font-sans',
};